<template>
  <div class="order-rate-shop-tabs">
    <div class="flex flex-gap-1 justify-content-between align-items-center">
      <div class="order-rate-shop-tabs-container">
        <RygenTabs
          :key="rygenTabsKey"
          v-model:active-tab-key="activeTabKey"
          :tabs="tabs"
        />

        <a
          v-if="routingGuideRule"
          v-tooltip.top="{
            value: t('routingGuides.inactiveWarning'),
            disabled: routingGuideRule.active,
          }"
          :href="
            generateRoutingGuideLink(
              routingGuideRule,
              selectedDomain?.naturalId,
            )
          "
          class="routing-guide-link border-right-1 border-color-divider"
          :class="{ 'inactive line-through': !routingGuideRule.active }"
        >
          {{ routingGuideRule.name }}
        </a>

        <RygenChip
          v-if="!isPolling && mileage"
          :label="
            t('rateShop.estimatedMileageCount', {
              param: t('rateShop.mileage', { count: mileage }),
            })
          "
          class="align-self-start"
        />
        <InfoIcon
          v-if="!isPolling && mileage === '-'"
          class="text-warning"
          icon="pi pi-exclamation-triangle"
          :tooltip="t('rateShop.warnings.mileageRequestRejected')"
        />
      </div>

      <SpotMarketHeaderActionButton
        v-if="activeTabKey === 'SPOT_MARKETS'"
        :can-request-spot-market-bid="order.status !== OrderStatus.CANCELED"
        :can-cancel-all-spot-market-bids="canCancelAllSpotMarketBids"
        :loading="isPolling || isBatchCancellingSpotMarkets"
        @cancel-all-spot-market-bids="onBatchCancelAllSpotMarkets"
        @request-spot-market-bid="
          emit('request-spot-market-bid', shipmentSpecId)
        "
      />
      <RateShopActionButton
        v-else-if="activeTabKey === 'RATES'"
        :spot-quotable="order.status !== OrderStatus.CANCELED"
        :updatable="order.status !== OrderStatus.CANCELED"
        :loading="isPolling"
        :brokerage-enabled="hasConfiguredBrokers"
        :waterfall-tender-enabled="order.status !== OrderStatus.CANCELED"
        :can-request-spot-market-bid="order.status !== OrderStatus.CANCELED"
        :is-rate-selection-reason-required="isRateSelectionReasonRequired"
        :loadboardable="order.status !== OrderStatus.CANCELED"
        :active-loadboards="activeLoadboardSources"
        @add-spot-quote="onAddSpotQuoteClicked"
        @post-to-loadboard="emit('post-to-loadboard')"
        @remove-loadboard="emit('remove-loadboard', $event)"
        @request-spot-market-bid="
          emit('request-spot-market-bid', shipmentSpecId)
        "
        @send-to-brokerage="emit('send-to-brokerage', {})"
        @update-rates="emit('rate-shop-update')"
        @waterfall-tender="isWaterfallTenderModalVisible = true"
      />
    </div>

    <Tabs v-model:value="activeTabKey" class="order-rate-shop-actual-tabs">
      <TabPanels>
        <TabPanel v-for="tab in tabs" :key="tab.key" :value="tab.key">
          <component
            :is="tab.component"
            v-if="tab.component"
            v-model:service-type-filters="serviceTypeFilters"
            :tab="tab"
            :order="order"
            :shipment-spec-id="shipmentSpecId"
            :rate-shop="rateShop"
            :rate-shop-loading="rateShopLoading"
            :spot-markets="spotMarketsForRateShop"
            :freight-allowance="freightAllowance"
            :active-loadboards="activeLoadboards"
            :selectable="order.status !== OrderStatus.CANCELED"
            :view-uplift="viewUplift"
            :updatable="order.status !== OrderStatus.CANCELED"
            :line-items-missing-dims="lineItemsMissingLtlInfo"
            @edit-quote="onEditQuote"
            @rate-selected="emit('rate-selected', $event)"
            @rate-shop-completed="onRateShopCompleted"
            @remove-loadboard="emit('remove-loadboard', $event)"
            @spot-market-cancelled="emit('spot-market-cancelled', $event)"
            @spot-market-resent="emit('spot-market-resent', $event)"
            @send-to-brokerage="emit('send-to-brokerage', $event)"
            @update:freight-allowance="emit('update:freight-allowance', $event)"
          />
        </TabPanel>
      </TabPanels>
    </Tabs>

    <EditQuoteRedesignModal
      v-if="arApFeatureFlagEnabled"
      v-model="spotQuote.data"
      v-model:visible="spotQuote.isShowingModal"
      :view-uplift="viewUplift"
      :deletable="
        !acceptedRateQuote || spotQuote.data?.id !== acceptedRateQuote.id
      "
      :equipment-type="order.equipmentType"
      @save="onSpotQuoteSaved"
      @delete="onSpotQuoteDeleted"
      @update:visible="onSpotQuoteModalClosed"
    />
    <EditQuoteModal
      v-else
      v-model="spotQuote.data"
      v-model:visible="spotQuote.isShowingModal"
      :view-uplift="viewUplift"
      :deletable="
        !acceptedRateQuote || spotQuote.data?.id !== acceptedRateQuote.id
      "
      :equipment-type="order.equipmentType"
      @save="onSpotQuoteSaved"
      @delete="onSpotQuoteDeleted"
      @update:visible="onSpotQuoteModalClosed"
    />

    <WaterfallTenderModal
      v-if="
        isWaterfallTenderingEnabled && order.status !== OrderStatus.CANCELED
      "
      v-model:visible="isWaterfallTenderModalVisible"
      :rate-shop="rateShop"
      @waterfall-tender="emit('waterfall-tender', $event)"
    />
  </div>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { useConfirm } from 'primevue/useconfirm';
import type { Component, PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useRygenTabs,
  useToast,
  useTooltip,
} from '@rygen/rygen-ui/composables';
import { Loadable, Tab } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  useHasConfiguredBrokers,
  useLowestCostReasonCode,
  useRateShop,
  useRoutingGuide,
} from '@/composables';
import {
  AcceptQuotePayload,
  FreightAllowance,
  Loadboard,
  LoadboardSource,
  Order,
  OrderStatus,
  RateQuote,
  RateShop,
  RateShopStatus,
  type ServiceTypeFilters,
  SpotMarket,
  WaterfallTenderConfig,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useSpotMarketStore } from '@/stores/SpotMarket';
import { generateRoutingGuideLink } from '@/util';

import OrderRateShopRatesTab from '@/components/order/rateShop/OrderRateShopRatesTab.vue';
import OrderRateShopSpotMarketsTab from '@/components/order/rateShop/OrderRateShopSpotMarketsTab.vue';
import EditQuoteRedesignModal from '@/components/rateShop/editQuote/EditQuoteRedesignModal.vue';
import EditQuoteModal from '@/components/rateShop/EditQuoteModal.vue';
import LoadboardBidsTab from '@/components/rateShop/loadboard/LoadboardBidsTab.vue';
import RateShopActionButton from '@/components/rateShop/RateShopActionButton.vue';
import SpotMarketHeaderActionButton from '@/components/rateShop/SpotMarketHeaderActionButton.vue';
import WaterfallTenderModal from '@/components/rateShop/WaterfallTenderModal.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import RygenTabs from '@rygen/rygen-ui/components/ui/RygenTabs.vue';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';

export type TabKey = 'RATES' | 'SPOT_MARKETS' | 'LOADBOARDS';

export interface OrderRateShopTab extends Tab<TabKey> {
  component: Component;
}

interface SpotQuoteData extends Loadable<RateQuote> {
  editIndex: number;
  isShowingModal: boolean;
}

const newSpotQuoteData = (): SpotQuoteData => ({
  data: new RateQuote({ source: 'SPOT_QUOTE' }),
  editIndex: -1,
  isShowingModal: false,
  loading: false,
});

const activeTabKey = defineModel('activeTabKey', {
  type: String as PropType<TabKey>,
  default: () => 'RATES',
});

const props = defineProps({
  order: {
    type: Order,
    default: () => new Order(),
  },
  shipmentSpecId: {
    type: Number,
    required: true,
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
  activeLoadboards: {
    type: Object as PropType<Loadable<Loadboard[]>>,
    default: () => ({ loading: false }),
  },
  rateShopLoading: {
    type: Boolean,
    default: () => false,
  },
  viewUplift: {
    type: Boolean,
    default: () => false,
  },
});
const {
  order,
  shipmentSpecId,
  rateShop,
  freightAllowance,
  activeLoadboards,
  rateShopLoading,
  viewUplift,
} = toRefs(props);

const emit = defineEmits<{
  (event: 'post-to-loadboard'): void;
  (event: 'rate-quote-updated', rateQuote: RateQuote): void;
  (event: 'rate-selected', payload: AcceptQuotePayload): void;
  (event: 'rate-shop-completed'): void;
  (event: 'rate-shop-update'): void;
  (event: 'remove-loadboard', source: LoadboardSource): void;
  (event: 'request-spot-market-bid', shipmentSpecId: number): void;
  (event: 'send-to-brokerage', payload: Partial<AcceptQuotePayload>): void;
  (event: 'spot-market-cancelled', spotMarketId: number): void;
  (event: 'spot-market-resent', spotMarketId: number): void;
  (event: 'spot-quote-added', rateQuote: RateQuote): void;
  (event: 'spot-quote-deleted', rateQuote: RateQuote): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
  (
    event: 'waterfall-tender',
    data: {
      configuration: WaterfallTenderConfig;
      callback: (success: boolean) => void;
    },
  ): void;
}>();

const serviceTypeFilters: Ref<ServiceTypeFilters> = ref({});

const arApFeatureFlagEnabled = useFlag('ar-ap-split-feat-flag');
const isWaterfallTenderingEnabled = useFlag('waterfallTendering');

const { selectedDomain } = useCurrentUserStore();
const confirm = useConfirm();
const { t } = useI18n();
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();
const { acceptedRateQuote, rateQuotes } = useRateShop(rateShop, {
  serviceTypeFilters,
});
const { routingGuideRule } = useRoutingGuide(rateShop, serviceTypeFilters);
const { loadSpotMarkets, spotMarkets } = useSpotMarketStore();
const toast = useToast();
const vTooltip = useTooltip();

const isBatchCancellingSpotMarkets = ref(false);
const spotQuote: Ref<SpotQuoteData> = ref(newSpotQuoteData());
const isWaterfallTenderModalVisible = ref(false);

const spotMarketsForRateShop = computed((): Loadable<SpotMarket[]> => {
  const rateShopId = rateShop.value.data?.id;
  const emptySpotMarket = { loading: false };

  return rateShopId
    ? (spotMarkets.value[rateShopId] ?? emptySpotMarket)
    : emptySpotMarket;
});

const tabs = computed((): OrderRateShopTab[] => [
  {
    key: 'RATES',
    header: t('rateShop.rates', 2),
    visible: true,
    component: OrderRateShopRatesTab,
  },
  {
    key: 'SPOT_MARKETS',
    header: t('carrierPortal.spotMarket.spotMarkets', 2),
    visible: !!spotMarketsForRateShop.value.data?.length,
    component: OrderRateShopSpotMarketsTab,
  },
  {
    key: 'LOADBOARDS',
    header: t('rateShop.loadboards', 2),
    visible: !!activeLoadboards.value.data?.length,
    component: LoadboardBidsTab,
  },
]);
const { visibleTabs } = useRygenTabs(tabs);

/** Remount when visible tabs change so PrimeVue scrollable TabList re-measures. */
const rygenTabsKey = computed(() =>
  visibleTabs.value.map((tab) => tab.key).join('|'),
);

const isPolling = computed(() => {
  return (
    rateShop.value.loading ||
    (rateShop.value.data?.status !== RateShopStatus.COMPLETED &&
      rateShop.value.data?.status !== RateShopStatus.ERROR &&
      !rateShop.value.error)
  );
});

const mileage = computed(() => {
  let mileage;
  if (order.value.totalMileage) {
    mileage = order.value.totalMileage;
  } else {
    mileage = rateQuotes.value?.map((x) => x.totalMileage).find((x) => !!x);
  }
  return mileage ? Math.round(mileage) : '-';
});

const canCancelAllSpotMarketBids = computed(
  () =>
    order.value.status !== OrderStatus.CANCELED &&
    (spotMarketsForRateShop.value.data?.some((sm) => sm.isCancellable) ??
      false),
);

const hasConfiguredBrokers = useHasConfiguredBrokers();

const lineItemsMissingLtlInfo = computed(() => {
  const lineItemsMissingFreightClass = order.value.lineItems.some(
    (li) => !li.freightClass,
  );
  const lineItemsMissingDimensions = order.value.lineItems.some(
    (li) => !li.heightDim || !li.widthDim || !li.lengthDim,
  );
  return lineItemsMissingDimensions || lineItemsMissingFreightClass;
});

const activeLoadboardSources = computed(
  () => activeLoadboards.value.data?.map((l) => l.source).filter(notNull) ?? [],
);

const onRateShopCompleted = () => {
  const rateShopId = rateShop.value.data?.id;
  if (!rateShopId) {
    return;
  }

  loadSpotMarkets(rateShopId, {
    shipmentSpecId: shipmentSpecId.value,
    bypassCache: false,
  });
  emit('rate-shop-completed');
};

const onAddSpotQuoteClicked = () => {
  spotQuote.value.data = new RateQuote({ source: 'SPOT_QUOTE' });
  spotQuote.value.isShowingModal = true;
};

const onEditQuote = (edittedSpotQuote: RateQuote) => {
  spotQuote.value.data = new RateQuote(edittedSpotQuote);
  spotQuote.value.isShowingModal = true;
};

const onSpotQuoteSaved = (rateQuote: RateQuote) => {
  if (!rateQuote.id) {
    emit('spot-quote-added', rateQuote);
  } else {
    emit('rate-quote-updated', rateQuote);
  }
};

const onSpotQuoteDeleted = (deletedSpotQuote: RateQuote) => {
  emit('spot-quote-deleted', deletedSpotQuote);
};

const onSpotQuoteModalClosed = (isVisible: boolean) => {
  if (!isVisible) {
    spotQuote.value = newSpotQuoteData();
  }
};

const onBatchCancelAllSpotMarkets = () => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('carrierPortal.spotMarket.cancelAllSpotMarkets'),
    message: t('carrierPortal.spotMarket.confirmCancelAllSpotMarkets'),
    accept: async () => {
      const rateShopId = rateShop.value.data?.id;
      if (!rateShopId) {
        return;
      }
      isBatchCancellingSpotMarkets.value = true;
      try {
        await api.shipmentSpecSpotMarketBatch.cancelSpotMarkets(
          shipmentSpecId.value,
        );
        await loadSpotMarkets(rateShopId, {
          bypassCache: true,
          shipmentSpecId: shipmentSpecId.value,
        });
      } catch (e) {
        toast.addError({
          detail: t('errors.cancelingParam', {
            param: t('carrierPortal.spotMarket.spotMarkets', 2),
          }),
          error: e,
        });
      } finally {
        isBatchCancellingSpotMarkets.value = false;
      }
    },
    acceptClass: 'p-button-sm p-button-danger',
    acceptIcon: 'pi pi-times',
    acceptProps: { iconPos: 'right' },
    acceptLabel: t('carrierPortal.spotMarket.cancelAllSpotMarkets'),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.dontCancelParam', {
      param: t('carrierPortal.spotMarket.spotMarkets', 2),
    }),
  });
};

watch(
  () => rateShop.value.data?.id,
  (newVal: number | undefined) => {
    if (newVal) {
      loadSpotMarkets(newVal, {
        shipmentSpecId: shipmentSpecId.value,
        bypassCache: true,
      });
    }
  },
  { immediate: true },
);
</script>

<style>
.order-rate-shop-tabs {
  --p-tabs-tabpanel-padding: 0;

  .order-rate-shop-tabs-container {
    display: flex;
    gap: var(--gap-1);
    align-items: center;

    .routing-guide-link {
      &.border-right-1 {
        padding-right: var(--sp-3);
      }
    }

    .rygen-chip {
      margin-block: auto;
    }
  }

  .order-rate-shop-actual-tabs {
    margin-top: var(--sp-6);

    .p-tabpanels {
      .p-tabpanel-active {
        .order-rate-shop-tab {
          display: flex;
          flex-direction: column;
          gap: var(--gap-1);
        }
      }
    }
  }
}
</style>
